import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Tag, Message, Checkbox,
  Select,
  Formblock,
  TableSkeleton,
  generateTableFromSchema,
  Modal,
} from '@zolteam/zol-admin-ui';
import { useMutation } from '@tanstack/react-query';
import i18n from '../../../i18n';
// services
import { updateCustomer, deleteCustomer } from '../../../services/customers/customersService';
import { resetPassword } from '../../../services/auth/authService';
import { useGetAdminListQuery } from '../../../services/users/usersQueries';
import { useGetPassengerListQuery } from '../../../services/customerPassengers/customerPassengerQueries';
import { deletePassenger } from '../../../services/customerPassengers/customerPassengersService';
// styles
import styles from './customerDetails.module.css';
// utils
import PassengerNew from '../passengerNew/PassengerNew';

const PassengerSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Passenger',
  properties: {
    fullname: {
      type: 'string',
      description: i18n.t('customerList.passengers.columns.fullname'),
    },
  },
  additionalProperties: false,
};

/**
 * Edit customer component form
 * @param {object} props
 * @param {Customer} props.customer
 * @param {() => void} [props.handleSuccess]
 * @param {() => void} [props.handleClosePanel]
 * @returns {JSX.Element}
 */
function CustomerDetails({ customer, handleSuccess = () => {}, handleClosePanel = () => {} }) {
  // hooks
  const { t } = useTranslation();
  // state
  const [shouldSendResetPasswordMail, setShouldSendResetPasswordMail] = useState(true);
  const [isPassengerModalOpen, setPassengerModalOpen] = useState(false);
  const [broker, setBroker] = useState(customer.broker?.id);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengerToDelete, setPassengerToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // api calls
  const { data: admins } = useGetAdminListQuery({
    pageNumber: 1,
    pageSize: 100,
  });

  const {
    data: passengers,
    isLoading: isLoadingPassengers,
    refetch: refetchPassengers,
  } = useGetPassengerListQuery(customer.id);

  const {
    mutate: mutateResetCustomerPassword,
    isLoading: isLoadingForgotPassword,
  } = useMutation(resetPassword, {
    onSuccess: () => {
      handleSuccess();
    },
  });

  const handleResetCustomerPassword = () => {
    mutateResetCustomerPassword({
      email: customer.email,
      returnUrl: `${window.location.origin}/update-password/worker?token=`,
      origin: 'worker',
    });
  };

  const {
    mutate: mutateUpdateCustomer,
    isLoading: isLoadingUpdateCustomer,
    isError: isErrorUpdateCustomer,
    error: errorUpdateCustomer,
  } = useMutation(updateCustomer, {
    onSuccess: () => {
      if (shouldSendResetPasswordMail && customer.status !== 'validate') {
        handleResetCustomerPassword();
      } else {
        handleSuccess();
      }
    },
  });

  const {
    mutate: mutateDeletePassenger,
    isLoading: isLoadingDeletePassenger,
  } = useMutation(deletePassenger, {
    onSuccess: () => {
      setPassengerToDelete(null);
      refetchPassengers();
    },
  });

  const {
    mutate: mutateDeleteCustomer,
    isLoading: isLoadingDeleteCustomer,
    error: deleteUserError,
    isError: isErrorDeleteCustomer,
  } = useMutation(deleteCustomer, {
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      handleClosePanel();
    },
  });

  const handleShouldSendUpdateCheck = () => setShouldSendResetPasswordMail((state) => !state);

  const handleValidateCustomer = () => {
    mutateUpdateCustomer({ ...customer, status: 'validate' });
  };

  /**
   * Handle the broker change.
   * @param {import('react').BaseSyntheticEvent} e
   * @returns {void}
   */
  const handleChangeBroker = (e) => {
    if (e?.target?.value) {
      setBroker(parseInt(e?.target?.value, 10));
      mutateUpdateCustomer({ ...customer, brokerId: parseInt(e?.target?.value, 10) });
    }
  };

  /**
   * Handle the open of the passenger modal.
   * @param {CustomerPassenger} passenger - The passenger to edit.
   */
  const handleOpenPassengerModal = (passenger = null) => {
    setSelectedPassenger(passenger);
    setPassengerModalOpen(true);
  };

  const handleClosePassengerModal = () => {
    setSelectedPassenger(null);
    setPassengerModalOpen(false);
  };

  const handlePassengerSuccess = () => {
    handleClosePassengerModal();
    refetchPassengers();
  };

  const handleGoToEdit = () => {
    localStorage.setItem('customerListUrl', window.location.pathname + window.location.search);
    window.location.href = `/customers/${customer.email}`;
  };

  /**
   * @param {CustomerPassenger} passenger
   */
  const handleDeletePassenger = (passenger) => {
    setPassengerToDelete(passenger);
  };

  const handleConfirmDelete = () => {
    if (passengerToDelete) {
      mutateDeletePassenger({
        passengerId: passengerToDelete.id,
        customerId: customer.id,
      });
    } else {
      mutateDeleteCustomer(customer.id);
    }
  };

  const handleDeleteCustomer = () => {
    setIsDeleteModalOpen(true);
  };

  const adminOptions = useMemo(() => admins?.data?.map(
    /**
     * @param {User} admin
     * @returns {{label: string | number, value: number | string}}
     */
    ({ firstname, lastname, id }) => ({ label: `${firstname} ${lastname}`, value: id }),
  ), [admins]);

  return (
    <div className="d-flex f-column justify-between full-screen-height">
      <div>
        {isErrorUpdateCustomer ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${errorUpdateCustomer
              // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}
        <div className="d-flex f-row f-wrap justify-between margin-bottom-m">
          <div className="d-flex f-row gap-s">
            <p className="h1-default margin-right-m">{`${customer.firstname} ${customer.lastname}`}</p>
            <Tag
              className="padding-right-s padding-left-s"
              label={t(`customerStatus.${customer.status}`)}
            />
          </div>
          <div className="d-flex f-row gap-xs">
            <Button
              onClick={handleGoToEdit}
              icon="edit"
              isLoading={isLoadingUpdateCustomer}
            />
            <Button
              color="alert"
              onClick={handleDeleteCustomer}
              icon="trash"
              isLoading={isLoadingDeleteCustomer}
            />
          </div>
        </div>
        <Formblock
          key="broker"
          htmlFor="broker"
        >
          <label htmlFor="broker" className="p2-default">
            <span>{t('customerList.fields.broker')}</span>
            <Select
              // @ts-ignore
              id="broker"
              name="broker"
              placeholder={t('customerList.fields.broker')}
              options={adminOptions}
              value={broker || ''}
              onChange={handleChangeBroker}
            />
          </label>
        </Formblock>
        <p className="p2-default">{t('customerList.columns.email')}</p>
        <p className="p1-default margin-bottom-s">{customer.email}</p>
        <p className="p2-default">{t('customerList.columns.phone')}</p>
        <p className="p1-default margin-bottom-s">{customer.phone}</p>
        <p className="p2-default">{t('customerList.columns.company')}</p>
        <p className="p1-default margin-bottom-s">{customer.company || '-'}</p>

        <div
          className="margin-top-m margin-bottom-m d-flex f-column gap-m"
        >
          <div className="d-flex f-row justify-between align-center">
            <h2 className="h2-default">{t('customerList.passengers.title')}</h2>
            <Button
              color="noBackground"
              icon="add"
              onClick={() => handleOpenPassengerModal()}
            >
              {t('customerList.passengers.actions.add')}
            </Button>
          </div>
          <div className={styles['large-table']}>
            {isLoadingPassengers ? <TableSkeleton /> : generateTableFromSchema({
              JSONSchema: PassengerSchema,
              data: passengers || [],
              labelNoResult: t('customerList.passengers.noResult'),
              customColumns: [
                {
                  key: 'category',
                  description: t('customerList.passengers.columns.category'),
                  sortable: false,
                  position: 'end',
                  renderCell: (/** @type {CustomerPassenger} */row) => (
                    <div className="text-center">{t(`customerList.passengers.categories.${row.category}`)}</div>
                  ),
                },
                {
                  key: 'identityFiles',
                  description: t('customerList.passengers.columns.documents'),
                  sortable: false,
                  position: 'end',
                  renderCell: (/** @type {CustomerPassenger} */row) => (
                    <div className="d-flex f-column gap-xs padding-s">
                      {row.identityFiles?.map((url, index) => (
                        <a className="underline" href={url} target="_blank" rel="noreferrer">
                          {t('customerList.passengers.fields.file', { nb: index + 1 })}
                        </a>
                      ))}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  description: ' ',
                  sortable: false,
                  position: 'end',
                  renderCell: (/** @type {CustomerPassenger} */row) => (
                    <div className={`margin-left-m d-flex gap-s ${styles['action-column']}`}>
                      <Button
                        color="noBackground"
                        icon="edit"
                        onClick={() => handleOpenPassengerModal(row)}
                      />
                      <Button
                        color="noBackground"
                        icon="trash"
                        onClick={() => handleDeletePassenger(row)}
                        isLoading={isLoadingDeletePassenger && passengerToDelete?.id === row.id}
                      />
                    </div>
                  ),
                },
              ],
            })}
          </div>
        </div>
      </div>
      <div className="gap-m d-flex f-column padding-bottom-m">
        {customer.status === 'validate' ? (
          <Button
            isLoading={isLoadingUpdateCustomer}
            onClick={handleResetCustomerPassword}
          >
            {t('customerList.actions.resetPassword')}
          </Button>
        )
          : (
            <Checkbox
              label={t('customerList.fields.shouldSendUpdatePasswordEmail')}
              checked={shouldSendResetPasswordMail}
              // @ts-ignore
              onChange={handleShouldSendUpdateCheck}
            />
          )}

        {customer.status !== 'validate' ? (
          <Button
            isLoading={isLoadingUpdateCustomer || isLoadingForgotPassword}
            onClick={handleValidateCustomer}
          >
            {t('customerList.actions.validate')}
          </Button>
        ) : null }
      </div>
      {isPassengerModalOpen ? (
        <Modal
          id="newPassengerModal"
          isOpen={isPassengerModalOpen}
          onClose={handleClosePassengerModal}
          title={t(selectedPassenger
            ? 'customerList.passengers.modal.titleEdit'
            : 'customerList.passengers.modal.title')}
        >
          <PassengerNew
            customerId={customer.id}
            onSuccess={handlePassengerSuccess}
            isUpdate={!!selectedPassenger}
            initialValues={selectedPassenger || null}
          />
        </Modal>
      ) : null}
      {passengerToDelete ? (
        <Modal
          id="deletePassengerModal"
          isOpen={!!passengerToDelete}
          onClose={() => setPassengerToDelete(null)}
          title={t('customerList.passengers.modal.deleteTitle')}
        >
          <div className="d-flex f-column gap-m">
            <p className="p1-default">
              {t('customerList.passengers.modal.deleteMessage')}
            </p>
            <div className="d-flex f-row justify-end gap-s">
              <Button
                color="neutral00"
                onClick={() => setPassengerToDelete(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="alert"
                onClick={handleConfirmDelete}
                isLoading={isLoadingDeletePassenger}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          id="deleteCustomerModal"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title={t('customerList.delete.title')}
          size="small"
        >
          {isErrorDeleteCustomer ? (
            <Message>
              {t(`apiErrors.${deleteUserError
              // @ts-ignore
                ?.code || deleteUserError?.code}`)}
            </Message>
          ) : null}
          <div className="d-flex f-column gap-m">
            <p className="p1-default">
              {t('customerList.delete.description')}
            </p>
            <div className="d-flex f-row justify-end gap-s">
              <Button
                color="neutral00"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="alert"
                onClick={handleConfirmDelete}
                isLoading={isLoadingDeleteCustomer}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export default CustomerDetails;
